// The Hall of Fame and Hall of Shame as plain text, for the share sheet.
//
// Two shapes. One record on its own, ranked, which is what people actually
// send ("look who has the worst week in league history"). And one half as a
// digest, naming the holder of each record, for the Sunday morning when the
// whole book updates at once.
//
// Managers go by first name, as in the standings share, and for the same
// reason: a group chat knows its people, not their squad names.
import { SHARE_SITE } from "./lineupShare";
import { displayNames } from "./standingsShare";

const MEDALS = ["🥇", "🥈", "🥉"];
const HALF = {
  fame: { emoji: "🏆", title: "Hall of Fame" },
  shame: { emoji: "💀", title: "Hall of Shame" },
};
// How many holders the digest names for one record: everyone tied for it, or
// the first few perfect weeks. Past this the line stops being a line.
const DIGEST_NAMES = 3;

const pts = (n) => `${n} ${Math.abs(n) === 1 ? "pt" : "pts"}`;
const isInstanceList = (kind) => kind === "perfectWeek";

// What a row says after the name: its score, and where it came from. A 5x
// Play names the team, since which team it went on is half the story. A
// perfect week is an achievement, not a score, so it has only the week.
function detail(row, kind, allTeams) {
  const week = row.week != null ? `Wk ${row.week}` : null;
  if (isInstanceList(kind)) return week ? `(${week})` : "";
  const team = kind === "tripPlay" || kind === "freeze" ? allTeams?.[row.teamSlug]?.school : null;
  const where = [team, week].filter(Boolean).join(", ");
  // A freeze is what it saved or cost, not a score: "saved 24 pts".
  const amount = kind === "freeze"
    ? `${row.value >= 0 ? "saved" : "cost"} ${pts(Math.abs(row.value))}`
    : pts(row.value);
  return `${amount}${where ? ` (${where})` : ""}`;
}

const entry = (name, row, kind, allTeams) => `${name} ${detail(row, kind, allTeams)}`.trim();

/**
 * One record, ranked. Shares exactly the rows it is given, so a card opened
 * out to ten shares ten.
 *
 * @param {object} record
 * @param {string} record.leagueName
 * @param {"fame"|"shame"} record.half
 * @param {string} record.label "Best Week", "Captain Calamity"
 * @param {string} record.kind as HallOfFame passes it to its list
 * @param {object[]} record.rows as useLeagueRecords returns them
 * @param {object} [record.allTeams] the teams map, for a 5x Play's team
 */
export function buildRecordShareText({ leagueName, half, label, kind, rows = [], allTeams }) {
  const names = displayNames(rows);
  const lines = [`${HALF[half].emoji} ${label} · ${leagueName}`, ""];

  rows.forEach((row, i) => {
    // Medals in Fame only: gold for the worst week in the league reads wrong.
    // Perfect weeks are not ranked against each other at all.
    const mark = isInstanceList(kind) ? "✨" : half === "fame" ? MEDALS[i] || `${i + 1}.` : `${i + 1}.`;
    lines.push(`${mark} ${entry(names[i], row, kind, allTeams)}`);
  });
  lines.push("", SHARE_SITE);

  return lines.join("\n");
}

/**
 * One half as a digest: each record and who holds it. A record nobody holds
 * yet is left out rather than listed empty.
 *
 * @param {object} hall
 * @param {string} hall.leagueName
 * @param {"fame"|"shame"} hall.half
 * @param {{ label: string, kind: string, rows: object[] }[]} hall.categories
 * @param {object} [hall.allTeams]
 */
export function buildHallShareText({ leagueName, half, categories = [], allTeams }) {
  const lines = [`${HALF[half].emoji} ${leagueName} · ${HALF[half].title}`, ""];

  const held = categories.filter((cat) => cat.rows?.length > 0);
  if (held.length === 0) lines.push("No records yet.");
  held.forEach(({ label, kind, rows }) => {
    const names = displayNames(rows);
    // Everyone level with the leader holds the record with them. Perfect
    // weeks have no leader, so the first few stand for the list.
    const holders = rows
      .map((row, i) => ({ row, name: names[i] }))
      .filter(({ row }) => isInstanceList(kind) || row.value === rows[0].value)
      .slice(0, DIGEST_NAMES);
    lines.push(`${label}: ${holders.map(({ row, name }) => entry(name, row, kind, allTeams)).join(", ")}`);
  });
  lines.push("", SHARE_SITE);

  return lines.join("\n");
}
